import gsap from "gsap";
import { Physics2DPlugin } from "gsap/dist/Physics2DPlugin";

import classes from "./MouseTrail.module.scss";
import { COLORS } from "./useMouseTrail";

const confettiCount = 30;
const confettiCooldown = 200;

gsap.registerPlugin(Physics2DPlugin);

/**
 * Creates a factory function that returns the `createConfetti` function.
 * This factory function allows us to keep track of `lastConfettiTime` across multiple calls.
 *
 * @returns {Function} - A function to create confetti at a specified position in a target element.
 */
export const createConfettiFactory = () => {
  let lastConfettiTime = 0;

  /**
   * Creates a confetti effect at a specified position within a target element.
   *
   * @param {number} positionX - The X coordinate of the click or event.
   * @param {number} positionY - The Y coordinate of the click or event.
   */
  return (positionX, positionY) => {
    const currentTime = Date.now();

    // Ensure the cooldown period between confetti triggers is respected
    if (currentTime - lastConfettiTime < confettiCooldown) {
      return;
    }

    lastConfettiTime = currentTime;

    // Create a document fragment to batch DOM updates
    const fragment = document.createDocumentFragment();

    // Create confetti elements using requestAnimationFrame for smooth batching
    let createdConfetti = 0;

    const createConfettiPieces = () => {
      if (createdConfetti >= confettiCount) {
        // Append the entire fragment to the body
        document.body.appendChild(fragment);
        return;
      }

      for (
        let i = 0;
        i < 5 && createdConfetti < confettiCount;
        i++, createdConfetti++
      ) {
        const confetti = document.createElement("div");
        confetti.classList.add(classes.confetti);
        confetti.style.backgroundColor =
          COLORS[createdConfetti % COLORS.length];

        const size = 5 + Math.random() * 10;
        confetti.style.width = `${size}px`;
        confetti.style.height = `${size}px`;

        // Change position to fixed
        confetti.style.position = "fixed";
        confetti.style.left = "0px";
        confetti.style.top = "0px";

        // Set initial confetti position
        gsap.set(confetti, {
          x: positionX,
          y: positionY,
        });

        // Append the confetti to the fragment
        fragment.appendChild(confetti);

        // Randomize the angle and velocity for each confetti piece
        const angle = Math.random() * 360;
        const velocity = 200 + Math.random() * 250;

        // Animate the confetti piece using GSAP Physics2DPlugin
        gsap.to(confetti, {
          duration: 2.5 + Math.random() * 1,
          physics2D: {
            angle,
            velocity,
            gravity: 450,
          },
          ease: "power2.out",
          onComplete: () => {
            confetti.remove();
          },
        });
      }

      // Schedule the next batch of confetti creation
      requestAnimationFrame(createConfettiPieces);
    };

    // Start creating confetti pieces in batches
    createConfettiPieces();
  };
};
